import React from 'react'
import { useAuth } from '../context/AuthContext'
import { Link, useLocation } from 'react-router-dom'
import {
  faAdd,
  faBars,
  faBookOpen,
  faBookReader,
  faBuilding,
  faHistory,
  faList,
  faUser,
  faPlay,
  faProjectDiagram,
  faQuestion,
  faSignOut,
  faUserAlt,
} from '@fortawesome/free-solid-svg-icons'
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons'
import DropdownButton, {
  MenuButton,
  MenuItem,
} from './navigation_bar/DropdownButton'
import DropdownButtonUser from './navigation_bar/DropdownButtonUser'
import { LineDivider } from './ui_reusable/styles/LineDivider.styles'
import AvatarIconStyle, {
  AvatarIcon,
} from './ui_reusable/styles/AvatarIcon.styles'

import {
  WhiteNavbar,
  LeftAlignedContainer,
  MiddleAlignedContainer,
  RightAlignedContainer,
  AppLogo,
  AppLogoCentered,
  LoggedOutBar,
  LoggedOutSide,
  UserProfile,
  UserInfo,
  UserName,
  UserEmail,
  StyledFontAwesomeIcon,
  ProjectListContainer,
  ProjectListElement,
  ProjectListTitle,
  ClientLogo,
  MenuItemNoLink,
} from './styles/NavigationBar.styles'

import {
  StandardRadio,
  RadioProvider,
} from './ui_reusable/StandardRadioButtons'
import {
  clientPickerLabel,
  projectPickerLabel,
  selectedProjectAllowsModelRun,
  useClientProjectContext,
} from '../context/ClientProjectContext'
import { useClientContext } from '../context/ClientContext'
import { useProjectContext } from '../context/ProjectContext'

/**
 * NavigationBar component for the whole app
 * Provides navigation menu, user controls, and project selection
 */
const Navigation: React.FC = () => {
  const location = useLocation()
  const currentPath = location.pathname
  const { isAuthenticated, user, isAdmin, isClient, isClientManager } =
    useAuth()

  const userName = user?.name || 'User name'
  const userEmail = user?.email || 'Email'

  const { clientProjectData } = useClientProjectContext()
  const { selectedClient, setSelectedClient } = useClientContext()
  const { selectedProject, setSelectedProject } = useProjectContext()
  const canCreateScenarios = selectedProjectAllowsModelRun(
    selectedProject,
    clientProjectData,
  )

  // Helper function to determine the initial radio value based on user type
  const getRadioInitValue = (): string => {
    if (!selectedProject) return 'null' // Using "null" as string instead of null

    return isAdmin
      ? `${selectedClient?.id}-${selectedProject?.id}`
      : selectedProject?.id?.toString() || 'null'
  }

  // Handle radio change for project selection
  const handleRadioChange = (value: string): void => {
    // This function can be used for additional logic when radio changes
    // Currently the individual onClick handlers handle the state changes
  }

  const refSelectAll = React.createRef<HTMLInputElement>()

  return (
    <WhiteNavbar className="navbar">
      {isAuthenticated && (
        <LeftAlignedContainer>
          <AppLogo to="/dashboard" className="AppLogo" />
        </LeftAlignedContainer>
      )}
      {isAuthenticated && (
        <MiddleAlignedContainer>
          <DropdownButton title="Insights">
            <MenuItem as={Link} to="/insights/saved_reports">
              <StyledFontAwesomeIcon icon={faBars} /> Saved reports
            </MenuItem>
            <MenuItem as={Link} to="/insights/saved_scenarios">
              <StyledFontAwesomeIcon icon={faBars} /> Saved scenarios
            </MenuItem>
          </DropdownButton>

          <DropdownButton title="Analyze">
            <MenuItem as={Link} to="/models/new">
              <StyledFontAwesomeIcon icon={faAdd} /> New model
            </MenuItem>
            <MenuItem
              as={Link}
              to="/scenarios/build"
              inactive={!canCreateScenarios}
              title={
                canCreateScenarios
                  ? undefined
                  : 'Reactivate the project before creating a scenario.'
              }
            >
              <StyledFontAwesomeIcon icon={faAdd} /> New scenario
            </MenuItem>
            <MenuItem as={Link} to="/models">
              <StyledFontAwesomeIcon icon={faList} /> Saved models
            </MenuItem>
            <MenuItem as={Link} to="/models/history">
              <StyledFontAwesomeIcon icon={faHistory} /> Model history
            </MenuItem>
          </DropdownButton>
          <MenuButton title="Databook" to="/datasets" />

          <DropdownButton title="Learn">
            <MenuItem
              as={Link}
              to="/intro"
              className={currentPath === '/intro' ? 'active' : ''}
            >
              <StyledFontAwesomeIcon icon={faPlay} /> Quick-start intro
            </MenuItem>
            <MenuItem
              as={Link}
              to="/academy"
              className={currentPath === '/academy' ? 'active' : ''}
            >
              <StyledFontAwesomeIcon icon={faBuilding} /> Citrus academy
            </MenuItem>
            <MenuItem
              as={Link}
              to="/faq"
              className={currentPath === '/faq' ? 'active' : ''}
            >
              <StyledFontAwesomeIcon icon={faQuestion} /> FAQ
            </MenuItem>
            <LineDivider />
            <MenuItem as={Link} inactive={true} to="/company-training">
              <StyledFontAwesomeIcon icon={faBookOpen} /> Company training
            </MenuItem>
          </DropdownButton>

          {isAdmin && (
            <DropdownButton title="Manage">
              <MenuItem
                as={Link}
                to="/projects"
                className={currentPath === '/projects' ? 'active' : ''}
              >
                <StyledFontAwesomeIcon icon={faProjectDiagram} /> Manage
                projects
              </MenuItem>
              {(isAdmin || (isClient && isClientManager)) && (
                <MenuItem
                  as={Link}
                  to="/clients"
                  className={currentPath === '/clients' ? 'active' : ''}
                >
                  <StyledFontAwesomeIcon icon={faUserAlt} /> Manage clients
                </MenuItem>
              )}
              <LineDivider />
              <MenuItem
                as={Link}
                to="/learning"
                className={currentPath.startsWith('/learning') ? 'active' : ''}
              >
                <StyledFontAwesomeIcon icon={faBookReader} /> Manage learning
              </MenuItem>
            </DropdownButton>
          )}
        </MiddleAlignedContainer>
      )}
      {isAuthenticated && user && (
        <RightAlignedContainer>
          {selectedClient && selectedClient.logo && (
            <ClientLogo
              src={selectedClient.logo}
              alt={`${selectedClient.name} logo`}
            />
          )}
          {/*
                    <IconButton>
                        <FontAwesomeIcon color={theme.colors.secondHighlight} size="sm" icon={faCircleQuestion} alt="help"/> }
                    </IconButton>
                    <IconButton>
                        <img src={bellSymbol} alt="Notifications" />
                    </IconButton>
                    */}
          <DropdownButtonUser>
            <MenuItem as={Link} to="/profile">
              <UserProfile>
                {user.avatar ? (
                  <AvatarIcon src={user.avatar} alt={user.name} />
                ) : (
                  <div css={AvatarIconStyle} />
                )}
                <UserInfo>
                  <UserName>{userName}</UserName>
                  <UserEmail>{userEmail}</UserEmail>
                </UserInfo>
              </UserProfile>
            </MenuItem>
            <LineDivider />
            <MenuItem
              as={Link}
              to="/profile"
              className={currentPath === '/profile' ? 'active' : ''}
            >
              <StyledFontAwesomeIcon icon={faUser} /> Profile settings
            </MenuItem>
            <MenuItem as={Link}>
              <StyledFontAwesomeIcon icon={faCircleQuestion} /> Help
            </MenuItem>
            {clientProjectData && clientProjectData.length > 0 && (
              <>
                <LineDivider />
                <MenuItemNoLink>Select project:</MenuItemNoLink>
                <ProjectListContainer>
                  <RadioProvider
                    group="projects"
                    initValue={getRadioInitValue()}
                    onChange={handleRadioChange}
                  >
                    <ProjectListElement
                      key={'all'}
                      onClick={(): void => {
                        refSelectAll.current?.click()
                        // For normal users, only reset selectedProject
                        if (isAdmin) {
                          setSelectedClient(null)
                          setSelectedProject(null)
                        } else {
                          setSelectedProject(null)
                        }
                      }}
                    >
                      <StandardRadio
                        ref={refSelectAll}
                        size="s"
                        group="projects"
                        value="null" // Using "null" as string instead of null
                      />
                      <ProjectListTitle>Select all</ProjectListTitle>
                    </ProjectListElement>

                    {clientProjectData.map((clientProject, i) => {
                      const radioRef = React.createRef<HTMLInputElement>()

                      // Use different values depending on user type
                      const radioValue = isAdmin
                        ? `${clientProject.client.id}-${clientProject.project.id}`
                        : clientProject.project.id?.toString() || ''

                      // Display different title depending on user type
                      const projectTitle = isAdmin
                        ? `${clientPickerLabel(clientProject.client.name, clientProject.client.is_active)} - ${projectPickerLabel(clientProject.project.name, clientProject.project.status)}`
                        : projectPickerLabel(
                            clientProject.project.name,
                            clientProject.project.status,
                          )

                      const handleClick = (): void => {
                        radioRef.current?.click()
                        setSelectedClient(clientProject.client)
                        setSelectedProject(clientProject.project)
                      }

                      return (
                        <ProjectListElement key={i} onClick={handleClick}>
                          <StandardRadio
                            ref={radioRef}
                            size="s"
                            group="projects"
                            value={radioValue}
                          />
                          <ProjectListTitle>{projectTitle}</ProjectListTitle>
                        </ProjectListElement>
                      )
                    })}
                  </RadioProvider>
                </ProjectListContainer>
              </>
            )}
            {/* <MenuItem as={Link} to="/teams" className={currentPath === "/teams" ? "active" : ""}>
                            <StyledFontAwesomeIcon icon={faUsers}/> Manage teams
                        </MenuItem> */}
            <LineDivider />
            <MenuItem as={Link} to="/logout">
              <StyledFontAwesomeIcon icon={faSignOut} /> Log out
            </MenuItem>
          </DropdownButtonUser>
        </RightAlignedContainer>
      )}
      {!isAuthenticated && (
        <LoggedOutBar>
          <LoggedOutSide />
          <AppLogoCentered to="/dashboard" className="AppLogo" />
          <LoggedOutSide $align="end">
            <MenuButton title="Login" to="/login" />
          </LoggedOutSide>
        </LoggedOutBar>
      )}
    </WhiteNavbar>
  )
}

export default Navigation
